import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowUpRight, Phone, Star, Clock, ShieldCheck, Sparkles } from "lucide-react";
import { api } from "../lib/api";

/**
 * SpecialPromo — dynamic flyer-driven landing page for any active Special.
 * Route: /promo/:promoCode  (e.g. /promo/maybach250)
 *
 * On mount:
 *   1. Fetch /api/specials/:code
 *   2. Cross-reference vehicle_ids against /api/vehicles
 *   3. Render flyer + qualifying vehicles
 * Clicking a vehicle routes to /book/ride/:id?promo=:code so BookRide + backend
 * apply the promo pricing automatically.
 */
export default function SpecialPromo() {
  const { promoCode } = useParams();
  const [special, setSpecial] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | ok | not_found
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [sp, veh] = await Promise.all([
          api.get(`/specials/${encodeURIComponent(promoCode)}`),
          api.get("/vehicles"),
        ]);
        if (cancelled) return;
        setSpecial(sp.data);
        const qualifying = (veh.data || []).filter((v) =>
          (sp.data.vehicle_ids || []).includes(v.id)
        );
        setVehicles(qualifying);
        setStatus("ok");
      } catch (e) {
        if (!cancelled) setStatus("not_found");
      }
    })();
    return () => { cancelled = true; };
  }, [promoCode]);

  const goBook = (id) => navigate(`/book/ride/${id}?promo=${encodeURIComponent(promoCode)}`);

  if (status === "loading") {
    return (
      <div className="h-96 flex items-center justify-center">
        <div className="w-10 h-10 rounded-full border-2 border-cyan border-t-transparent animate-spin" />
      </div>
    );
  }

  if (status === "not_found") {
    return (
      <section className="mx-auto max-w-2xl px-6 py-24 text-center">
        <Sparkles size={32} className="text-cyan mx-auto mb-4" />
        <h1 className="font-serif text-4xl mb-3">This special isn’t running right now</h1>
        <p className="text-white/60 mb-6">
          The promo code <span className="text-cyan font-mono">{promoCode}</span> is inactive
          or has ended. Check our other offers or book at regular rates.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/fleet"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-cyan text-midnight text-sm font-medium"
          >
            Browse fleet
          </Link>
          <a
            href="tel:+19298993467"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/20 text-sm text-white/80"
          >
            <Phone size={14} /> Call 929-899-3467
          </a>
        </div>
      </section>
    );
  }

  return (
    <section
      data-testid="special-promo-page"
      className="mx-auto max-w-6xl px-5 sm:px-8 py-10 sm:py-14"
    >
      {/* Flyer */}
      {special.flyer_url && (
        <div className="rounded-3xl overflow-hidden border border-white/10 shadow-[0_20px_80px_-20px_rgba(57,214,255,0.35)]">
          <img
            src={special.flyer_url}
            alt={special.title}
            data-testid="special-promo-flyer"
            className="w-full h-auto block"
          />
        </div>
      )}

      {/* Headline */}
      <div className="text-center mt-10 sm:mt-14 mb-8">
        <div className="label-caps text-cyan text-[11px] tracking-[0.3em] mb-3 inline-flex items-center gap-2">
          <Sparkles size={12} /> Limited-time promo
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl leading-tight">
          {special.title}<br />
          <span className="text-cyan">${special.hourly_rate}/hour</span>
          <span className="text-white/50 text-2xl sm:text-3xl">
            {" · "}{special.min_hours}-hour minimum
          </span>
        </h1>
        {special.subtitle && (
          <p className="mt-4 text-white/60 text-sm sm:text-base max-w-2xl mx-auto">
            {special.subtitle}
          </p>
        )}
      </div>

      {/* Perks strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10">
        {[
          { icon: Star, label: "Executive comfort" },
          { icon: ShieldCheck, label: "Professional drivers" },
          { icon: Sparkles, label: "Luxury experience" },
          { icon: Clock, label: "On-time, every time" },
        ].map(({ icon: I, label }) => (
          <div
            key={label}
            className="glass rounded-2xl p-4 border border-white/5 flex items-center gap-3"
          >
            <div className="w-9 h-9 rounded-lg bg-cyan/10 text-cyan flex items-center justify-center">
              <I size={16} />
            </div>
            <div className="text-xs sm:text-sm text-white/80">{label}</div>
          </div>
        ))}
      </div>

      {/* Vehicle picks */}
      {vehicles.length === 0 ? (
        <div className="glass rounded-2xl p-8 text-center text-white/60">
          No qualifying vehicles are currently available.
        </div>
      ) : (
        <div className={`grid gap-6 ${vehicles.length === 1 ? "grid-cols-1 max-w-2xl mx-auto" : "grid-cols-1 md:grid-cols-2"}`}>
          {vehicles.map((v) => (
            <button
              key={v.id}
              type="button"
              data-testid={`special-promo-pick-${v.id}`}
              onClick={() => goBook(v.id)}
              className="group text-left glass rounded-3xl overflow-hidden border border-white/10 hover:border-cyan/60 transition-all shadow-lg hover:shadow-[0_20px_60px_-15px_rgba(57,214,255,0.45)]"
            >
              <div className="aspect-[16/10] bg-ink-800 relative overflow-hidden">
                {v.image_url ? (
                  <img
                    src={v.image_url}
                    alt={v.name}
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/30">
                    {v.name}
                  </div>
                )}
                <div className="absolute top-3 left-3">
                  <span className="label-caps text-[10px] bg-cyan text-midnight rounded-full px-3 py-1 font-bold">
                    Special · ${special.hourly_rate}/hr
                  </span>
                </div>
              </div>
              <div className="p-5">
                <div className="font-serif text-2xl leading-tight mb-1">{v.name}</div>
                <div className="text-white/50 text-xs label-caps mb-4">
                  {v.category?.replace("-", " ") || "vehicle"}
                </div>
                <div className="flex items-center justify-between">
                  <div className="text-sm text-white/70">
                    {Number(v.hourly_rate) !== Number(special.hourly_rate) && (
                      <span className="line-through text-white/30 mr-2">
                        ${Number(v.hourly_rate || 0).toFixed(0)}/hr
                      </span>
                    )}
                    <span className="text-cyan font-medium">${special.hourly_rate}/hr</span>
                    <span className="text-white/50"> · {special.min_hours}-hr min</span>
                  </div>
                  <div className="inline-flex items-center gap-1 text-cyan text-xs label-caps group-hover:translate-x-0.5 transition-transform">
                    Book at special rate <ArrowUpRight size={14} />
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Fallback CTA */}
      <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
        <a
          href="tel:+19298993467"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-cyan text-midnight text-sm font-medium"
        >
          <Phone size={16} /> Call to book · 929-899-3467
        </a>
        <Link
          to="/fleet"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/20 text-sm text-white/80 hover:text-white hover:border-white/40"
        >
          Browse full fleet
        </Link>
      </div>
    </section>
  );
}
